import { ArrowRight, Check, KeyRound, Smartphone } from "lucide-react";
import { motion } from "motion/react";
import {
	BrowserFrame,
	ExampleTag,
	PhoneFrame,
} from "@/components/blocks/frames/Frames";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";
import {
	StageScene,
	stageBar,
	stageItem,
	stagePop,
	stageRise,
	type UseCaseSceneProps,
} from "./StageScene";

/** Access, diagnosis, compliance, release. Server renders the compliance. */
const DURATIONS = [4000, 3800, 4200, 3800] as const;

/** Dependencies kept as native code rather than replaced. */
const KEPT = "d";

/**
 * Taking over an orphaned React Native app: accesses recovered in the
 * client's name, native dependencies sorted, store requirements met, and the
 * update published.
 */
export function TakeoverScene(props: UseCaseSceneProps) {
	const { dict } = useI18n();
	const scene = dict.useCases.items.takeover.scene;
	const tag = dict.common.illustrationBadge;

	const steps = [
		<BrowserFrame
			key="access"
			host={scene.access.file}
			tag={tag}
			className="w-full"
			contentClassName="p-4"
		>
			<p className="mb-3 flex items-center gap-2 text-sm font-semibold">
				<KeyRound className="size-4 text-signal-ink" />
				{scene.access.title}
			</p>
			<ul className="space-y-2">
				{Object.entries(scene.access.items).map(([key, item], index) => (
					<motion.li
						key={key}
						variants={stageItem}
						custom={index}
						className="flex items-center gap-3 rounded-lg border bg-background px-3 py-2"
					>
						<motion.span
							variants={stagePop}
							custom={0.5 + index * 0.25}
							className="flex size-4 shrink-0 items-center justify-center rounded-full bg-signal text-signal-foreground"
						>
							<Check className="size-2.5" strokeWidth={3} />
						</motion.span>
						<span className="min-w-0 flex-1 truncate text-[11px] font-medium">
							{item.name}
						</span>
						<span className="shrink-0 rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[9.5px] text-muted-foreground">
							{item.status}
						</span>
					</motion.li>
				))}
			</ul>
			<p className="mt-3 font-mono text-[9.5px] text-signal-ink">
				{scene.access.owner}
			</p>
		</BrowserFrame>,

		<BrowserFrame
			key="diagnosis"
			host={scene.diagnosis.file}
			tag={tag}
			className="w-full"
			contentClassName="p-4"
		>
			<p className="mb-3 text-sm font-semibold">{scene.diagnosis.title}</p>
			<ul className="space-y-2">
				{Object.entries(scene.diagnosis.rows).map(([key, row], index) => {
					const kept = key === KEPT;
					return (
						<motion.li
							key={key}
							variants={stageItem}
							custom={index}
							className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,0.8fr)] items-center gap-2 rounded-lg border bg-background px-3 py-2"
						>
							<span
								className={cn(
									"truncate font-mono text-[10px]",
									!kept && "text-muted-foreground line-through",
								)}
							>
								{row.from}
							</span>
							<ArrowRight className="size-3 text-muted-foreground" />
							<motion.span
								variants={stagePop}
								custom={0.6 + index * 0.25}
								className={cn(
									"justify-self-start truncate rounded-full px-2 py-0.5 text-[9.5px] font-medium",
									kept
										? "bg-foreground/[0.07]"
										: "bg-signal text-signal-foreground",
								)}
							>
								{row.to}
							</motion.span>
						</motion.li>
					);
				})}
			</ul>
			<motion.p
				variants={stageRise}
				custom={5}
				className="mt-3 rounded-lg bg-muted/70 px-3 py-2 text-[10px] font-medium"
			>
				{scene.diagnosis.summary}
			</motion.p>
		</BrowserFrame>,

		<BrowserFrame
			key="compliance"
			host={scene.compliance.file}
			tag={tag}
			className="w-full"
			contentClassName="p-4"
		>
			<p className="mb-3 text-sm font-semibold">{scene.compliance.title}</p>
			<ul className="space-y-2.5">
				{Object.entries(scene.compliance.rows).map(([key, row], index) => (
					<li
						key={key}
						className="grid grid-cols-[minmax(0,1fr)_5.25rem_1rem] items-center gap-3 text-[10.5px]"
					>
						<span className="truncate font-medium">{row.label}</span>
						<span className="flex items-center justify-end gap-1.5 font-mono text-[10px]">
							<span className="text-muted-foreground line-through">
								{row.from}
							</span>
							<ArrowRight className="size-2.5 text-muted-foreground" />
							<span className="font-semibold">{row.to}</span>
						</span>
						<motion.span
							variants={stagePop}
							custom={0.9 + index * 0.3}
							className="flex size-4 items-center justify-center rounded-full bg-signal text-signal-foreground"
						>
							<Check className="size-2.5" strokeWidth={3} />
						</motion.span>
						<span className="col-span-3 block h-1 overflow-hidden rounded-full bg-foreground/[0.07]">
							<motion.span
								variants={stageBar}
								custom={index}
								className="block h-full origin-left rounded-full bg-signal"
							/>
						</span>
					</li>
				))}
			</ul>
			<motion.span
				variants={stagePop}
				custom={2.3}
				className="mt-4 flex w-fit items-center gap-1.5 rounded-full border border-signal/50 bg-signal/10 px-2.5 py-1 text-[10px] font-semibold text-signal-ink"
			>
				<Check className="size-3" strokeWidth={3} />
				{scene.compliance.compliant}
			</motion.span>
		</BrowserFrame>,

		<PhoneFrame key="release" className="h-full w-auto max-w-none">
			<div className="flex h-full flex-col gap-3 px-3.5 pt-2 pb-3">
				<ExampleTag className="self-start px-1.5 text-[8px]">{tag}</ExampleTag>
				<div className="flex items-center gap-2.5">
					<span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-foreground text-background">
						<Smartphone className="size-5" />
					</span>
					<div className="min-w-0">
						<p className="truncate text-[12px] font-semibold">
							{scene.release.appName}
						</p>
						<p className="font-mono text-[9.5px] text-muted-foreground">
							{scene.release.version}
						</p>
					</div>
				</div>
				<motion.span
					variants={stagePop}
					custom={0.4}
					className="flex h-7 items-center justify-center rounded-full bg-signal text-[10px] font-semibold text-signal-foreground"
				>
					{scene.release.update}
				</motion.span>
				<div className="rounded-lg border px-2.5 py-2">
					<p className="text-[10px] font-semibold">{scene.release.whatsNew}</p>
					<p className="mt-1 text-[9.5px] leading-snug text-muted-foreground">
						{scene.release.notes}
					</p>
				</div>
				<ul className="space-y-1.5">
					{Object.entries(scene.release.builds).map(([key, label], index) => (
						<motion.li
							key={key}
							variants={stageItem}
							custom={index + 2}
							className="flex items-center gap-2 text-[9.5px]"
						>
							<motion.span
								variants={stagePop}
								custom={0.9 + index * 0.25}
								className="flex size-3.5 shrink-0 items-center justify-center rounded-full bg-signal text-signal-foreground"
							>
								<Check className="size-2" strokeWidth={3} />
							</motion.span>
							{label}
						</motion.li>
					))}
				</ul>
				<p className="mt-auto rounded-lg bg-muted/70 px-2.5 py-2 text-center text-[9.5px] font-medium">
					{scene.release.published}
				</p>
			</div>
		</PhoneFrame>,
	];

	return (
		<StageScene
			{...props}
			labels={Object.values(scene.labels)}
			durations={DURATIONS}
			initial={2}
			render={(step) => steps[step]}
		/>
	);
}
